/**
 * @file Sentry helper.
 */

import * as Sentry from '@sentry/browser';

import getConfig from './config';
import getLogger from './logger';

const log = getLogger('Sentry');

/**
 * Initializes Sentry.
 */
const initSentry = () => {
  const config = getConfig();
  const { sentry, app } = config;

  if (!sentry) {
    return;
  }

  log.info('initSentry() :: Initializing Sentry...');

  Sentry.init({
    dsn: sentry,
    release: `web@${app.version}`,
    whitelistUrls: [/https?:\/\/d1ldwnjkukjs70\.cloudfront\.net/i],
    ignoreErrors: [
      'NS_ERROR_NOT_INITIALIZED',
      'PADDINGXX',
      'DataCloneError',
      'ObservableQuery with this id',
      'SecurityError: Attempt to use history.replaceState',
    ],
  });
};

/**
 * Captures a Sentry message.
 *
 * @param {String} message - The message.
 * @param {Object} info - Additional info.
 * @param {String} info.level - The severity level.
 * @param {Object} [info.extra] - Any extra information that should be sent to Sentry along with the message.
 */
const sentryCaptureMessage = (message, info = {}) => {
  if (!message) {
    throw new Error('Message is required');
  }

  const { level, extra } = info;

  Sentry.withScope(scope => {
    scope.setLevel(level || 'log');
    scope.setExtras(extra);

    Sentry.captureMessage(message);
  });
};

/**
 * Captures a Sentry exception.
 *
 * @param {Error} error - The error.
 * @param {Object} [extra] - Any extra information to be passed along with the error.
 */
const sentryCaptureException = (error, extra) => {
  let eventId = '';

  Sentry.withScope(scope => {
    scope.setExtras(extra);

    eventId = Sentry.captureException(error);
  });

  return eventId;
};

export { initSentry, sentryCaptureMessage, sentryCaptureException };
